import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SignalBriefVerdict } from "../../api/client.ts";
import { VERDICTS } from "./ResearchVerdictChip.tsx";

// Research confidence is the research agent's 1-5 score of how sure it is of
// its verdict, whatever that verdict is (`signal_briefs.confidence`). It says
// nothing about how good the signal is: a confident "disqualified" is a 5.
// Distinct from the matcher's match strength (see MatchStrengthSelect).
export const RESEARCH_CONFIDENCE_SCALE =
  "How sure the research agent is of its verdict, 1–5. Says nothing about how good the signal is: a confident Disqualified is a 5.";

export const CONFIDENCE_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: "Any confidence" },
  { value: 3, label: "≥ 3 (plausible)" },
  { value: 4, label: "≥ 4 (well supported)" },
  { value: 5, label: "5 (certain)" },
];

/** When a research agent's verdict fires the step chained under it. */
export default function VerdictCondition({
  verdicts,
  minConfidence,
  onChange,
  disabled,
}: {
  verdicts: SignalBriefVerdict[];
  minConfidence: number | null;
  onChange: (next: { verdicts: SignalBriefVerdict[]; min_confidence: number | null }) => void;
  disabled?: boolean;
}) {
  const toggle = (verdict: SignalBriefVerdict) =>
    onChange({
      verdicts: verdicts.includes(verdict)
        ? verdicts.filter((v) => v !== verdict)
        : [...verdicts, verdict],
      min_confidence: minConfidence,
    });

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        When the research verdict is
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {VERDICTS.map((v) => (
          <Tooltip key={v.value} title={v.hint}>
            <Chip
              size="small"
              label={v.label}
              color={verdicts.includes(v.value) ? "primary" : "default"}
              variant={verdicts.includes(v.value) ? "filled" : "outlined"}
              onClick={disabled ? undefined : () => toggle(v.value)}
            />
          </Tooltip>
        ))}
      </Stack>
      {verdicts.length === 0 ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          Pick at least one verdict.
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Verified: every check passed. Qualified: none failed, some unclear (its caveats).
        </Typography>
      )}
      <TextField
        label="Research confidence"
        select
        size="small"
        value={minConfidence ?? ""}
        onChange={(e) =>
          onChange({
            verdicts,
            min_confidence: e.target.value === "" ? null : Number(e.target.value),
          })
        }
        helperText={`${RESEARCH_CONFIDENCE_SCALE} Applies to the verdicts picked above.`}
        disabled={disabled}
        fullWidth
      >
        {CONFIDENCE_OPTIONS.map((opt) => (
          <MenuItem key={opt.label} value={opt.value === null ? "" : opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
